/** @typedef {import('headlight-configurable-application').Pict} Pict */
// @ts-ignore
const PictProvider = require('pict-provider');

class CommentProvider extends PictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {import('pict')} */
		this.pict;
		/** @type {any} */
		this.log;
	}

	getParentCommentKey(pIDParent)
	{
		return `ParentKey~${pIDParent}`;
	}

	/**
	 * Get comments by customer ID
	 * @param {Number} pIDCustomer - The ID of the customer to get comments for
	 * @param {Number} pIDDocument - The ID of the document to get comments for
	 * @param {Array<Number>} pCommentIDs - The IDs of the comments to get
	 * @param {Function} fCallback - The callback function to call when the comments are fetched
	 * @async
	 */
	getCommentStructure(pIDCustomer, pIDDocument, pCommentIDs, fCallback)
	{
		this.pict.log.info(`I am getting all comments for ${pIDCustomer}`);

		// @ts-ignore
		const tmpAnticipate = this.pict.newAnticipate();

		const tmpCommentDataStructureState = {
			CommentIDs: [],
			Comments: [],
			ParentCommentMap: {},
			MergedComments: [],
			Users: [],
		};

		// Step 1: Fetch commentIDs for Document
		tmpAnticipate.anticipate(function(fNext)
		{
			// force the cache to expire for this document so it picks up the latest comments
			this.pict.EntityProvider?.recordSetCache?.['Document']?.expire(`FBV~IDDocument~EQ~${pIDDocument}`);
			// force the cache to expire for the comment id's so it picks up the latest comments
			this.pict.EntityProvider?.recordSetCache?.['Comment']?.expire(`FBL~IDComment~INN~${pCommentIDs.join(',')}`);
			// Get the comment IDs for the document
			this.pict.EntityProvider.getEntitySet(
				'Document',
				`FBV~IDDocument~EQ~${pIDDocument}`,
				function(pError, pRecordSet)
				{
					if (pError)
					{
						this.log.error(`Error downloading data for document ${pIDDocument}: ${pError}`, pError);
						return fCallback(pError);
					}
					// Check actual structure of pRecordSet
					const firstRecord = Array.isArray(pRecordSet) ? pRecordSet[0] : (pRecordSet.Entities ? pRecordSet.Entities[0] : pRecordSet);
					tmpCommentDataStructureState.CommentIDs = (firstRecord && firstRecord.Comments) ? firstRecord.Comments : [];

					fNext();
				}.bind(this),
			);
		}.bind(this));

		// Step 2: Get comment data for each comment by Document ID
		tmpAnticipate.anticipate(function(fNext)
		{
			if (tmpCommentDataStructureState.CommentIDs.length === 0)
			{
				this.pict.log.info('No comments found for the document.');
				return fNext([]);
			}

			const commentIDs = tmpCommentDataStructureState.CommentIDs.join(',');
			this.pict.EntityProvider.getEntitySet(
				'Comment',
				`FBL~IDComment~INN~${commentIDs}`,
				function(pError, pRecordSet)
				{
					if (pError)
					{
						this.pict.log.error(`Error downloading comments: ${pError}`, pError);
						return fCallback(pError);
					}

					tmpCommentDataStructureState.Comments = pRecordSet;
					fNext();
				}.bind(this),
			);
		}.bind(this));

		// Step 3: Fetch user data for customer
		tmpAnticipate.anticipate(function(fNext)
		{
			// fetch all the users that appear in the comments
			if (tmpCommentDataStructureState.Comments.length === 0)
			{
				this.pict.log.info('No comments found, skipping user fetch.');
				tmpCommentDataStructureState.Users = [];
				return fNext();
			}
			// Collect unique user IDs from comments (rather than fetching all users)
			const userIDs = tmpCommentDataStructureState.Comments.reduce((acc, comment) =>
			{
				if (comment.CreatingIDUser && !acc.includes(comment.CreatingIDUser))
				{
					acc.push(comment.CreatingIDUser);
				}
				if (comment.UpdatingIDUser && !acc.includes(comment.UpdatingIDUser))
				{
					acc.push(comment.UpdatingIDUser);
				}
				return acc;
			}, []);
			this.pict.EntityProvider.getEntitySet(
				'User',
				`FBL~IDUser~INN~${userIDs.join(',')}`,
				function(pError, pRecordSet)
				{
					if (pError)
					{
						this.log.error(`Error downloading users for customer ${pIDCustomer}: ${pError}`, pError);
						return fCallback(pError);
					}

					tmpCommentDataStructureState.Users = pRecordSet;
					fNext();
				}.bind(this),
			);
		}.bind(this));

		// Step 4: Transform and nest comments
		tmpAnticipate.anticipate(function(fNext)
		{
			const { Comments, ParentCommentMap, MergedComments } = tmpCommentDataStructureState;

			// Build map of comments by ID
			for (const raw of Comments)
			{
				// for each comment, we need to find the user who created it and the user who last updated it
				const creatingUser = tmpCommentDataStructureState.Users.find((user) => user.IDUser === raw.CreatingIDUser);
				const updatingUser = tmpCommentDataStructureState.Users.find((user) => user.IDUser === raw.UpdatingIDUser);
				if (creatingUser)
				{
					raw.CreatingUserData = creatingUser;
				}
				if (updatingUser)
				{
					raw.UpdatingUserData = updatingUser;
				}

				const structured = {
					IDComment: raw.IDComment,
					CreateDate: raw.CreateDate,
					CreatingIDUser: raw.CreatingIDUser,
					CreatingUserData: raw.CreatingUserData || null,
					UpdatingIDUser: raw.UpdatingIDUser,
					UpdatingUserData: raw.UpdatingUserData || null,
					Date: raw.UpdateDate,
					Entity: raw.Entity || '',
					IDParent: raw.IDParent || 0,
					IDEntity: raw.IDEntity || 0,
					Comment: raw.Comment,
					CommentHtml: raw.CommentHtml,
					Children: [],
					_RowID: `Comment_${raw.IDComment}`,
				};

				ParentCommentMap[structured.IDComment] = structured;
			}

			// Nest comments under their parents
			for (const id in ParentCommentMap)
			{
				if (Object.prototype.hasOwnProperty.call(ParentCommentMap, id))
				{
					const comment = ParentCommentMap[id];
					if (comment.IDParent && ParentCommentMap[comment.IDParent])
					{
						ParentCommentMap[comment.IDParent].Children.push(comment);
					}
					else
					{
						MergedComments.push(comment);
					}
				}
			}

			fNext(MergedComments);
		});

		tmpAnticipate.wait(fCallback);
	}

	/**
	 * Get comments by entity
	 * @param {Number} pIDEntity - The ID of the entity to get comments for. Currently doesn't exist - created for future use.
	 * @param {Function} pCallback - The callback function to call when the comments are fetched
	 * @return {Promise}
	 * @async
	 */
	async getCommentsByEntity(pIDEntity, pCallback)
	{
		try
		{
			return await new Promise((resolve, reject) =>
			{
				// force the cache to expire for this entity so it picks up the latest comments
				this.pict.EntityProvider?.recordSetCache?.['Entity']?.expire(`FBV~IDEntity~EQ~${pIDEntity}`);
				// Get the comments for the entity
				this.fable.services.HeadlightRestClient.getJSON(`Comments/By/IDEntity/${pIDEntity}`,
					(pError, pBody) =>
					{
						if (pError)
						{
							this.pict.log.error(`Error fetching comments: `, pError);
							reject(pError);
							// if we have a callback, run it now
							if (pCallback)
							{
								pCallback(null);
							}
						}
						else
						{
							resolve(pBody);
							// if we have a callback, run it now
							if (pCallback)
							{
								pCallback(pBody);
							}
						}
					});
			});
		}
		catch (error)
		{
			this.pict.log.error(`Error fetching comments: `, error);
			// if we have a callback, run it now
			if (pCallback)
			{
				pCallback(null);
			}
		}
	}

	/**
	 * Get comments by customer ID
	 * @param {Number} pIDCustomer - The ID of the customer to get comments for
	 * @param {Function} pCallback - The callback function to call when the comments are fetched
	 * @return {Promise}
	 * @async
	 */
	async getCommentsByCustomer(pIDCustomer, pCallback)
	{
		try
		{
			return await new Promise((resolve, reject) =>
			{
				// force the cache to expire for this customer so it picks up the latest comments
				this.pict.EntityProvider?.recordSetCache?.['Customer']?.expire(`FBV~IDCustomer~EQ~${pIDCustomer}`);
				// Get the comments for the customer
				this.fable.services.HeadlightRestClient.getJSON(`Comments/By/IDCustomer/${pIDCustomer}`,
					(pError, pBody) =>
					{
						if (pError)
						{
							this.pict.log.error(`Error fetching comments: `, pError);
							reject(pError);
							// if we have a callback, run it now
							if (pCallback)
							{
								pCallback(null);
							}
						}
						else
						{
							resolve(pBody);
							// if we have a callback, run it now
							if (pCallback)
							{
								pCallback(pBody);
							}
						}
					});
			});
		}
		catch (error)
		{
			this.pict.log.error(`Error fetching comments: `, error);
			// if we have a callback, run it now
			if (pCallback)
			{
				pCallback(null);
			}
		}
	}

	/**
	 * Get comments by comment ID
	 * @param {Number} pIDComment - The ID of the customer to get comments for
	 * @param {Function} pCallback - The callback function to call when the comments are fetched
	 * @return {Promise}
	 * @async
	 */
	async getCommentByID(pIDComment, pCallback)
	{
		try
		{
			return await new Promise((resolve, reject) =>
			{
				// force the cache to expire for this comment so it picks up the latest changes on comment edit actions
				this.pict.EntityProvider?.recordSetCache?.['Comment']?.expire(`${pIDComment}`);
				// Get the comment by ID
				this.fable.services.HeadlightRestClient.getJSON(`Comment/${pIDComment}`,
					(pError, pBody) =>
					{
						if (pError)
						{
							this.pict.log.error(`Error fetching comment by ID: `, pError);
							reject(pError);
							// if we have a callback, run it now
							if (pCallback)
							{
								pCallback(null);
							}
						}
						else
						{
							resolve(pBody);
							// if we have a callback, run it now
							if (pCallback)
							{
								pCallback(pBody);
							}
						}
					});
			});
		}
		catch (error)
		{
			this.pict.log.error(`Error fetching comment by ID: `, error);
			// if we have a callback, run it now
			if (pCallback)
			{
				pCallback(null);
			}
		}
	}

	/**
	 * Add a new comment to the server.
	 * @param {Object} pComment - The new comment to post
	 * @param {Function} fOnSaveResult - The callback function to call when the project file is posted
	 */
	async createComment(pComment, fOnSaveResult)
	{
		const tmpURL = `Comment/`;
		await new Promise((resolve, reject) => this.fable.services.HeadlightRestClient.postJSON(tmpURL, pComment,
			(pError, pBody) =>
			{
				if (pError)
				{
					this.pict.log.error(`Error creating comment: `, pError);
					this.pict.views.HeadlightPanels.toast({
						message: 'An error occurred creating the comment.',
						type: 'is-danger',
					});
					this.pict.providers.UtilityProvider.removeAllLoadingClasses();
					reject(new Error(`Error creating comment: ` + pError));
				}
				else if (pBody && pBody.Error)
				{
					this.pict.log.error(`Error creating comment: `, pBody.Error);
					this.pict.views.HeadlightPanels.toast({
						message: 'An error occurred creating the comment.',
						type: 'is-danger',
					});
					this.pict.providers.UtilityProvider.removeAllLoadingClasses();
					reject(new Error(`Error creating comment: ` + pBody.Error));
				}
				else if (pBody)
				{
					resolve(pBody);
					fOnSaveResult(pBody);
				}
			},
		));
	}

	/**
	 * Post an update to a comment to the server.
	 * @param {Object} pIDComment - The updated IDComment to post
	 * @param {Function} fOnSaveResult - The callback function to call when the comment is posted
	 */
	async updateComment(pIDComment, fOnSaveResult)
	{
		const tmpURL = `Comment/`;
		// force the cache to expire for this comment so it picks up the latest changes on comment edit actions
		this.pict.EntityProvider?.recordSetCache?.['Comment']?.expire(`${pIDComment}`);
		// update the comment
		await new Promise((resolve, reject) => this.fable.services.HeadlightRestClient.putJSON(tmpURL, pIDComment,
			(pError, pBody) =>
			{
				if (pError)
				{
					this.pict.log.error(`Error updating comment: `, pError);
					this.pict.views.HeadlightPanels.toast({
						message: 'An error occurred updating the comment.',
						type: 'is-danger',
					});
					this.pict.providers.UtilityProvider.removeAllLoadingClasses();
					reject(new Error(`Error updating comment: ` + pError));
				}
				else if (pBody && pBody.Error)
				{
					this.pict.log.error(`Error updating comment: `, pBody.Error);
					this.pict.views.HeadlightPanels.toast({
						message: 'An error occurred updating the comment.',
						type: 'is-danger',
					});
					this.pict.providers.UtilityProvider.removeAllLoadingClasses();
					reject(new Error(`Error updating comment: ` + pBody.Error));
				}
				else if (pBody)
				{
					resolve(pBody);
					fOnSaveResult(pBody);
				}
			},
		));
	}

	/**
	 * Attach a comment to a document. This route, unlike a document update, is open on a locked document.
	 * @param {number} pIDDocument - The document to attach the comment to
	 * @param {number} pIDComment - The comment to attach
	 * @param {Function} fOnSaveResult - The callback function to call with the updated document
	 */
	async addCommentToDocument(pIDDocument, pIDComment, fOnSaveResult)
	{
		const tmpURL = `Document/${pIDDocument}/Comment/Add/${pIDComment}`;
		// force the cache to expire for this document so it picks up the latest comments
		this.pict.EntityProvider?.recordSetCache?.['Document']?.expire(`FBV~IDDocument~EQ~${pIDDocument}`);
		await new Promise((resolve, reject) => this.fable.services.HeadlightRestClient.getJSON(tmpURL,
			(pError, pBody) =>
			{
				if (pError)
				{
					this.pict.log.error(`Error updating comment record for document: `, pError);
					this.pict.views.HeadlightPanels.toast({
						message: 'An error occurred updating the comment record attached to this document.',
						type: 'is-danger',
					});
					this.pict.providers.UtilityProvider.removeAllLoadingClasses();
					reject(new Error(`Error updating comment record for document: ` + pError));
				}
				else if (pBody && pBody.Error)
				{
					this.pict.log.error(`Error updating comment record for document: `, pBody.Error);
					this.pict.views.HeadlightPanels.toast({
						message: 'An error occurred updating the comment record attached to this document.',
						type: 'is-danger',
					});
					this.pict.providers.UtilityProvider.removeAllLoadingClasses();
					reject(new Error(`Error updating comment record for document: ` + pBody.Error));
				}
				else if (pBody)
				{
					resolve(pBody);
					fOnSaveResult(pBody);
				}
			},
		));
	}

	/**
	 * Delete a comment.
	 * @param {number} pIDComment - The comment to delete
	 * @param {Function} fOnDeleteResult - The callback function to call when the project file is posted
	 */
	deleteComment(pIDComment, fOnDeleteResult)
	{
		if (!pIDComment)
		{
			this.pict.log.error('pIDComment was not passed.');
			return;
		}
		this.fable.HeadlightRestClient.deleteEntity('Comment', pIDComment, (pError, pBody) =>
		{
			if (pError)
			{
				this.pict.log.error('Failed to delete comment.', pError);
				this.pict.views.HeadlightPanels.toast({
					message: 'Failed to delete comment!',
					type: 'is-danger',
				});
				fOnDeleteResult(pError);
			}
			else
			{
				this.pict.log.info('Comment deleted successfully.', pBody);
				if (typeof fOnDeleteResult === 'function')
				{
					fOnDeleteResult(pBody);
				}
			}
		});
	}
}

module.exports = CommentProvider;
module.exports.default_configuration = {};
